import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Send, Ban, RotateCcw, Download, Eye, X, Archive, ArchiveRestore, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, downloadFile, money, money2, dateShort } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionTitle, Field } from "@/components/Bits";
import { InvoiceStatus } from "@/pages/Invoices";
import InvoiceLinkPanel from "@/components/InvoiceLinkPanel";
import ClientInvoiceBlocks from "@/components/ClientInvoiceBlocks";
import SquareCheckout from "@/components/SquareCheckout";

const input = "w-full rounded-sm border border-line bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-lime";

function PaidConfirmation({ invoice, payment, onDone }) {
  const balance = payment.invoice?.balance ?? invoice.balance;
  const number = payment.invoice_number || invoice.number;
  return (
    <div className="card-surface p-8 text-center sm:p-12" data-testid="payment-success">
      <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#EDEFC4]">
        <Check className="h-7 w-7 text-lime-dark" strokeWidth={1.5} />
      </span>
      <h2 className="mt-6 font-serif text-3xl text-ink">Thank you — payment received.</h2>
      <p className="mt-3 text-sm text-stone">
        We’ve charged {money2(payment.amount)}
        {payment.card_brand ? ` to your ${payment.card_brand}${payment.last_4 ? ` ending ${payment.last_4}` : ""}` : ""}.
        {balance > 0
          ? ` Your remaining balance on ${number} is ${money2(balance)}.`
          : ` Invoice ${number} is now paid in full.`}
      </p>
      <p className="mt-2 text-xs text-fog">A confirmation is on its way to your inbox.</p>
      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        <button data-testid="payment-success-done" onClick={onDone}
          className="rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">Back to invoice</button>
        <button data-testid="payment-success-receipt-pdf"
          onClick={() => downloadFile(`/payments/${payment.id}/receipt`, `Receipt-${number}.pdf`)
            .catch((e) => toast.error(apiError(e)))}
          className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-5 py-2.5 text-sm text-ink hover:bg-bone">
          <Download className="h-4 w-4" strokeWidth={1.5} /> Download receipt
        </button>
        {payment.receipt_url && (
          <a data-testid="payment-receipt-link" href={payment.receipt_url} target="_blank" rel="noreferrer"
            className="rounded-sm border border-line bg-white px-5 py-2.5 text-sm text-ink hover:bg-bone">View Square receipt</a>
        )}
      </div>
    </div>
  );
}

function PayPanel({ invoice, onPaid }) {
  const [amount, setAmount] = useState(invoice.balance);
  const [custom, setCustom] = useState(false);
  const minimum = Number(invoice.minimum_payment) || 0;
  const valid = amount > 0 && amount <= invoice.balance + 0.001 && (!minimum || amount >= minimum || amount >= invoice.balance);

  return (
    <div className="space-y-6" data-testid="pay-panel">
      <div className="card-surface p-6 sm:p-7">
        <div className="overline">Payment summary</div>
        <dl className="mt-5 space-y-3 text-sm">
          <div className="flex justify-between"><dt className="text-stone">Invoice total</dt><dd className="num text-ink">{money2(invoice.total)}</dd></div>
          <div className="flex justify-between"><dt className="text-stone">Already paid</dt><dd className="num text-lime-dark">{money2(invoice.amount_paid)}</dd></div>
          <div className="flex justify-between border-t border-line pt-3">
            <dt className="text-ink">Balance due</dt><dd className="num text-lg text-ink" data-testid="pay-balance">{money2(invoice.balance)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-stone">Paying now</dt>
            <dd className="num text-lg text-lime-dark" data-testid="pay-amount">{money2(amount || 0)}</dd>
          </div>
        </dl>
        {invoice.allow_partial && (
          <div className="mt-6 border-t border-line pt-5">
            {!custom ? (
              <button data-testid="pay-custom-toggle" onClick={() => setCustom(true)}
                className="text-xs uppercase tracking-[0.15em] text-stone hover:text-ink">Pay a different amount</button>
            ) : (
              <Field label="Amount to pay" hint={minimum ? `Minimum ${money2(minimum)}` : "Any amount up to the balance"}>
                <input data-testid="pay-amount-input" type="number" step="0.01" min="0" max={invoice.balance}
                  value={amount} onChange={(e) => setAmount(Number(e.target.value))} className={`num ${input}`} />
              </Field>
            )}
          </div>
        )}
        {!valid && (
          <p className="mt-4 text-xs text-[#A8492E]" data-testid="pay-amount-warning">
            Enter an amount between {money2(minimum || 0.01)} and {money2(invoice.balance)}.
          </p>
        )}
      </div>
      {valid && <SquareCheckout invoice={invoice} amount={Number(amount)} onPaid={onPaid} />}
    </div>
  );
}

function RefundControl({ payment, onDone }) {
  const [open, setOpen] = useState(false);
  const refundable = Number((payment.amount - (payment.refunded_amount || 0)).toFixed(2));
  const [amount, setAmount] = useState(refundable);
  const openControl = () => {
    setAmount(refundable);
    setOpen(true);
  };
  const submit = async () => {
    try {
      await api.post(`/payments/${payment.id}/refund`, { amount: Number(amount), reason: "Studio refund" });
      toast.success("Refund submitted to Square");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  if (!open)
    return (
      <button data-testid={`refund-btn-${payment.id}`} onClick={openControl}
        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-stone hover:text-clay">
        <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.5} /> Refund
      </button>
    );
  return (
    <div className="flex items-center gap-2">
      <input data-testid={`refund-amount-${payment.id}`} type="number" step="0.01" value={amount}
        onChange={(e) => setAmount(e.target.value)} className={`num w-28 ${input}`} />
      <button data-testid={`refund-confirm-${payment.id}`} onClick={submit}
        className="rounded-sm bg-ink px-3 py-2 text-xs text-white">Confirm</button>
      <button onClick={() => setOpen(false)} className="text-xs text-stone hover:text-ink">Cancel</button>
    </div>
  );
}

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isStaff, isAdmin } = useAuth();
  const [inv, setInv] = useState(null);
  const [paying, setPaying] = useState(false);
  const [done, setDone] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [history, setHistory] = useState([]);
  const [clientView, setClientView] = useState(null);
  const payRef = useRef(null);

  const openPay = () => {
    setPaying(true);
    setTimeout(() => payRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/invoices/${id}`);
      setInv(data);
      if (isStaff) api.get(`/invoices/${id}/reminders`).then((r) => setReminders(r.data)).catch(() => {});
      if (isStaff) api.get(`/invoices/${id}/history`).then((r) => setHistory(r.data)).catch(() => {});
    } catch (e) {
      toast.error(apiError(e));
      navigate("/invoices");
    }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!inv) return <div className="py-24 text-center text-sm text-fog">Loading invoice…</div>;

  const openClientPreview = async () => {
    try {
      const { data } = await api.get(`/invoices/${id}/client-preview`);
      setClientView(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const act = async (path, label) => {
    try {
      await api.post(`/invoices/${id}/${path}`);
      toast.success(label);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const onPaid = (payment) => {
    setPaying(false);
    setDone(payment);
    // Reflect the new status straight away; the reload below confirms it from the server.
    if (payment.invoice) setInv((prev) => ({ ...prev, ...payment.invoice }));
    load();
  };

  const payable = !isStaff && ["Sent", "Partially Paid", "Overdue"].includes(inv.status) && inv.balance > 0;

  return (
    <div className="space-y-10" data-testid="invoice-detail-page">
      <button onClick={() => navigate("/invoices")} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-stone transition-colors hover:text-ink">
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> All invoices
      </button>

      <div className="flex flex-col gap-6 border-b border-line pb-8 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="overline mb-3">{inv.invoice_type} · <span className="num">{inv.number}</span></div>
          <h1 data-testid="invoice-title" className="font-serif text-3xl tracking-tight text-ink sm:text-4xl">{inv.title}</h1>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-stone">
            <Link to={`/projects/${inv.project_id}`} className="hover:text-lime-dark">{inv.project_name}</Link>
            <span className="text-line">·</span>
            <InvoiceStatus status={inv.status} testId="invoice-detail-status" />
            {inv.due_date && <><span className="text-line">·</span><span className="num">Due {dateShort(inv.due_date)}</span></>}
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          {inv.status !== "Draft" && (
            <button data-testid="invoice-pdf-btn"
              onClick={() => downloadFile(`/invoices/${id}/pdf`, `Invoice-${inv.number}.pdf`)
                .catch((e) => toast.error(apiError(e)))}
              className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-4 py-2.5 text-sm text-ink transition-colors hover:border-lime">
              <Download className="h-4 w-4" strokeWidth={1.5} /> Invoice PDF
            </button>
          )}
          {isStaff && inv.status === "Draft" && (
            <>
              <button data-testid="invoice-send-btn" onClick={() => act("send", "Invoice sent to your client")}
                className="inline-flex items-center gap-2 rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">
                <Send className="h-4 w-4" strokeWidth={1.5} /> Send to Client
              </button>
            </>
          )}
          {isStaff && ["Sent", "Overdue"].includes(inv.status) && (
            <button data-testid="invoice-void-btn" onClick={() => act("void", "Invoice voided — its payment link no longer works")}
              className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-4 py-2.5 text-sm text-ink hover:bg-line/50">
              <Ban className="h-4 w-4" strokeWidth={1.5} /> Void
            </button>
          )}
          {isStaff && (
            inv.archived ? (
              <button data-testid="invoice-unarchive-btn" onClick={() => act("unarchive", "Invoice restored to your active list")}
                className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-4 py-2.5 text-sm text-ink hover:bg-line/50">
                <ArchiveRestore className="h-4 w-4" strokeWidth={1.5} /> Restore
              </button>
            ) : (
              <button data-testid="invoice-archive-btn" onClick={() => act("archive", "Invoice archived — payments and links are untouched")}
                className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-4 py-2.5 text-sm text-ink hover:bg-line/50">
                <Archive className="h-4 w-4" strokeWidth={1.5} /> Archive
              </button>
            )
          )}
          {isAdmin && ["Draft", "Void"].includes(inv.status) && !(inv.amount_paid > 0) && (
            <button data-testid="invoice-delete-btn"
              onClick={async () => {
                if (!window.confirm(`Delete ${inv.number} permanently? This cannot be undone — archive it instead to keep the record.`)) return;
                try {
                  await api.delete(`/invoices/${id}`);
                  toast.success(`${inv.number} deleted`);
                  navigate("/invoices");
                } catch (e) {
                  toast.error(apiError(e));
                }
              }}
              className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-4 py-2.5 text-sm text-clay hover:border-clay">
              <Trash2 className="h-4 w-4" strokeWidth={1.5} /> Delete
            </button>
          )}
          {payable && !paying && !done && (
            <button data-testid="invoice-pay-btn" onClick={openPay}
              className="rounded-sm bg-lime px-6 py-3 text-sm font-medium text-ink transition-colors hover:bg-lime-dark">
              Pay {money2(inv.balance)}
            </button>
          )}
        </div>
      </div>

      {clientView && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 sm:items-center sm:p-6" data-testid="client-preview-modal">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-t-sm bg-white p-7 sm:rounded-sm sm:p-10">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <span className="overline">Client view</span>
                <h3 className="mt-2 font-serif text-2xl text-ink">{clientView.title}</h3>
                <p className="mt-1 text-xs text-fog">Invoice {clientView.number} · {clientView.project_name}</p>
              </div>
              <button
                type="button"
                data-testid="client-preview-close"
                onClick={() => setClientView(null)}
                className="rounded-sm border border-line p-1.5 text-stone transition-colors hover:border-fog"
                aria-label="Close"
              >
                <X className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
            <div className="space-y-5">
              <ClientInvoiceBlocks invoice={clientView} testPrefix="client-preview-group" />
            </div>
            {clientView.notes && <p className="mt-5 text-xs leading-relaxed text-stone">{clientView.notes}</p>}
            <p className="mt-6 text-xs text-fog">Generated from the same client representation the portal and PDF use.</p>
          </div>
        </div>
      )}

      {done ? (
        <PaidConfirmation invoice={inv} payment={done} onDone={() => setDone(null)} />
      ) : (
        <div className="grid gap-10 lg:grid-cols-[1.4fr_1fr]">
          <div className="space-y-10 lg:order-1">{/* detail column */}
            <section>
              <SectionTitle>Invoice Detail</SectionTitle>
              {isStaff && (
                <div className="mb-4 flex flex-wrap gap-3">
                  <button
                    type="button"
                    data-testid="preview-as-client-btn"
                    onClick={openClientPreview}
                    className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-4 py-2 text-xs uppercase tracking-[0.12em] text-stone transition-colors hover:border-lime hover:text-ink"
                  >
                    <Eye className="h-3.5 w-3.5" strokeWidth={1.5} /> Preview as client
                  </button>
                  {!!(inv.source_items || []).length && (
                    <span className="inline-flex items-center text-xs text-fog" data-testid="sourcing-linked-note">
                      {inv.source_items.length} linked selection{inv.source_items.length === 1 ? "" : "s"}
                      {inv.grouping ? ` · grouped by ${inv.grouping}` : ""}
                    </span>
                  )}
                </div>
              )}
              <div className="card-surface overflow-hidden">
                <table className="w-full text-sm" data-testid="invoice-line-items">
                  <tbody>
                    {(inv.line_items || []).map((l, i) => (
                      <tr key={i} className="border-b border-line">
                        <td className="px-6 py-4 text-ink">{l.description}</td>
                        <td className="num px-6 py-4 text-right text-ink">{money2(l.amount)}</td>
                      </tr>
                    ))}
                    <tr className="border-b border-line">
                      <td className="px-6 py-3 text-stone">Subtotal</td>
                      <td className="num px-6 py-3 text-right">{money2(inv.subtotal)}</td>
                    </tr>
                    {inv.tax > 0 && (
                      <tr className="border-b border-line">
                        <td className="px-6 py-3 text-stone">Tax</td>
                        <td className="num px-6 py-3 text-right">{money2(inv.tax)}</td>
                      </tr>
                    )}
                    <tr className="bg-bone">
                      <td className="px-6 py-4 font-medium text-ink">Total</td>
                      <td className="num px-6 py-4 text-right text-lg text-ink" data-testid="invoice-total">{money2(inv.total)}</td>
                    </tr>
                  </tbody>
                </table>
                {inv.notes && <p className="border-t border-line px-6 py-5 text-sm leading-relaxed text-stone">{inv.notes}</p>}
              </div>
            </section>

            {isStaff && <InvoiceLinkPanel invoiceId={id} status={inv.status} />}

            {isAdmin && inv.internal_summary && (
              <section data-testid="invoice-internal-summary">
                <SectionTitle>Internal Summary</SectionTitle>
                <div className="card-surface grid grid-cols-2 gap-5 p-7 sm:grid-cols-4">
                  <div><span className="overline block">Sourcing cost</span><span className="num text-lg text-ink">{money2(inv.internal_summary.internal_cost)}</span></div>
                  <div><span className="overline block">Client billing</span><span className="num text-lg text-ink">{money2(inv.internal_summary.client_billed)}</span></div>
                  <div><span className="overline block">Gross profit</span><span className="num text-lg text-lime-dark">{money2(inv.internal_summary.estimated_profit)}</span></div>
                  <div><span className="overline block">Margin</span><span className="num text-lg text-ink">{inv.internal_summary.margin_percentage}%</span></div>
                </div>
                <p className="mt-3 text-xs text-fog">Visible to admins only — never included in anything the client receives.</p>
              </section>
            )}

            {isStaff && !!history.length && (
              <section data-testid="invoice-history">
                <SectionTitle>Invoice History</SectionTitle>
                <div className="card-surface divide-y divide-line">
                  {history.map((h) => (
                    <div key={h.id} className="flex flex-wrap items-center justify-between gap-3 px-6 py-3 text-sm">
                      <span className="text-ink capitalize">{h.action}{h.detail ? ` · ${h.detail}` : ""}</span>
                      <span className="text-xs text-fog">{h.by_name} · {dateShort(h.at)}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section>
              <SectionTitle>Payment History</SectionTitle>
              <div className="card-surface divide-y divide-line" data-testid="invoice-payments">
                {(inv.payments || []).map((p) => (
                  <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 text-sm">
                    <div>
                      <div className="num text-ink">{money2(p.amount)}</div>
                      <div className="mt-1 text-xs text-fog">
                        {p.card_brand || p.method || "Card"}{p.last_4 ? ` ending ${p.last_4}` : ""} · {dateShort(p.created_at)}
                        {p.refunded_amount > 0 ? ` · ${money2(p.refunded_amount)} refunded` : ""}
                      </div>
                      {isStaff && p.failure_reason && (
                        <div className="mt-1 text-xs text-[#A8492E]" data-testid={`payment-failure-${p.id}`}>
                          {p.failure_reason}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                      <span className="text-[11px] uppercase tracking-[0.15em] text-stone">{p.status}</span>
                      {["COMPLETED", "APPROVED"].includes(p.status) && (
                        <button data-testid={`receipt-pdf-${p.id}`}
                          onClick={() => downloadFile(`/payments/${p.id}/receipt`, `Receipt-${inv.number}.pdf`)
                            .catch((e) => toast.error(apiError(e)))}
                          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-stone transition-colors hover:text-ink">
                          <Download className="h-3.5 w-3.5" strokeWidth={1.5} /> Receipt
                        </button>
                      )}
                      {isStaff && <span className="num break-all text-[11px] text-fog">{p.square_payment_id}</span>}
                      {isAdmin && p.status === "COMPLETED" && p.amount > (p.refunded_amount || 0) && (
                        <RefundControl payment={p} onDone={load} />
                      )}
                    </div>
                  </div>
                ))}
                {!(inv.payments || []).length && (
                  <div className="px-6 py-10 text-center text-sm text-fog">No payments on this invoice yet.</div>
                )}
              </div>
            </section>
          </div>

          <div className="space-y-8 lg:order-2" ref={payRef}>
            {paying ? (
              <PayPanel invoice={inv} onPaid={onPaid} />
            ) : (
              <div className="card-surface p-7" data-testid="invoice-balance-card">
                <div className="overline">Balance</div>
                <div className="num mt-4 text-4xl text-ink">{money2(inv.balance)}</div>
                <dl className="mt-7 space-y-3 border-t border-line pt-5 text-sm">
                  <div className="flex justify-between"><dt className="text-stone">Total</dt><dd className="num">{money2(inv.total)}</dd></div>
                  <div className="flex justify-between"><dt className="text-stone">Paid</dt><dd className="num text-lime-dark">{money2(inv.amount_paid)}</dd></div>
                  {inv.amount_refunded > 0 && (
                    <div className="flex justify-between"><dt className="text-stone">Refunded</dt><dd className="num text-clay">{money2(inv.amount_refunded)}</dd></div>
                  )}
                </dl>
                {payable && (
                  <button data-testid="invoice-pay-btn-side" onClick={openPay}
                    className="mt-7 w-full rounded-sm bg-lime px-5 py-3.5 text-sm font-medium text-ink transition-colors hover:bg-lime-dark">
                    Pay securely
                  </button>
                )}
                {isStaff && inv.internal_notes && (
                  <p className="mt-7 border-t border-line pt-5 text-xs leading-relaxed text-fog" data-testid="invoice-internal-notes">
                    Internal · {inv.internal_notes}
                  </p>
                )}
                {isStaff && reminders.length > 0 && (
                  <div className="mt-7 border-t border-line pt-5" data-testid="invoice-reminders">
                    <div className="overline">Reminders sent</div>
                    <ul className="mt-3 space-y-1.5 text-xs text-fog">
                      {reminders.map((r) => (
                        <li key={r.id}>
                          {{ upcoming: "Three days before", due: "On the due date", overdue: "Overdue notice" }[r.stage] || r.stage}
                          {" · "}{dateShort(r.sent_at)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
